import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './styles.scss';

// Main DropdownMenu component (root container)
const DropdownMenu = ({
  children,
  open,
  onOpenChange,
  defaultOpen = false,
  className = '',
  style,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const handleOpenChange = (newOpen) => {
    if (onOpenChange) {
      onOpenChange(newOpen);
    } else {
      setIsOpen(newOpen);
    }
  };

  const actualOpen = open !== undefined ? open : isOpen;

  return (
    <div
      className={`dropdown-menu ${className}`}
      data-state={actualOpen ? 'open' : 'closed'}
      style={style}
    >
      {React.Children.map(children, (child) =>
        React.cloneElement(child, {
          isOpen: actualOpen,
          onOpenChange: handleOpenChange,
        })
      )}
    </div>
  );
};

// Trigger component
const DropdownMenuTrigger = ({
  children,
  asChild = false,
  isOpen,
  onOpenChange,
  disabled = false,
  className = '',
}) => {
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      onOpenChange?.(!isOpen);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!disabled) {
        onOpenChange?.(!isOpen);
      }
    } else if (e.key === 'Escape' && isOpen) {
      onOpenChange?.(false);
    }
  };

  const triggerProps = {
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    'aria-expanded': isOpen,
    'aria-haspopup': 'menu',
    'data-state': isOpen ? 'open' : 'closed',
    disabled,
    className: `dropdown-menu-trigger ${className}`.trim(),
    tabIndex: disabled ? -1 : 0,
    role: 'button',
  };

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      ...triggerProps,
      className: `${children.props.className || ''} ${
        triggerProps.className
      }`.trim(),
    });
  }

  return (
    <button type="button" {...triggerProps}>
      {children}
    </button>
  );
};

// Content component
const DropdownMenuContent = ({
  children,
  isOpen,
  onOpenChange,
  align = 'start',
  side = 'bottom',
  sideOffset = 4,
  alignOffset = 0,
  className = '',
  avoidCollisions = true,
  forceMount = false,
}) => {
  const contentRef = useRef(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!isOpen || !mounted) return;

    const handleClickOutside = (event) => {
      if (contentRef.current && !contentRef.current.contains(event.target)) {
        const trigger = event.target.closest('.dropdown-menu-trigger');
        if (!trigger) {
          onOpenChange?.(false);
        }
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onOpenChange?.(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onOpenChange, mounted]);

  if (!mounted && !forceMount) return null;
  if (!isOpen && !forceMount) return null;

  const contentClasses = [
    'dropdown-menu-content',
    `dropdown-menu-content--${side}`,
    `dropdown-menu-content--${align}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const style = {
    '--side-offset': `${sideOffset}px`,
    '--align-offset': `${alignOffset}px`,
  };

  return (
    <div
      ref={contentRef}
      className={contentClasses}
      style={style}
      data-state={isOpen ? 'open' : 'closed'}
      data-side={side}
      data-align={align}
      role="menu"
      aria-orientation="vertical"
    >
      {children}
    </div>
  );
};

// Item component
// const DropdownMenuItem = ({
//   children,
//   onSelect,
//   disabled = false,
//   className = '',
//   asChild = false,
//   ...props
// }) => {
//   const handleClick = (e) => {
//     if (disabled) {
//       e.preventDefault();
//       return;
//     }
//     onSelect?.(e);
//   };

//   const handleKeyDown = (e) => {
//     if (disabled) return;

//     if (e.key === 'Enter' || e.key === ' ') {
//       e.preventDefault();
//       onSelect?.(e);
//     }
//   };

//   const itemProps = {
//     ...props,
//     onClick: handleClick,
//     onKeyDown: handleKeyDown,
//     className: `dropdown-menu-item ${
//       disabled ? 'dropdown-menu-item--disabled' : ''
//     } ${className}`.trim(),
//     role: 'menuitem',
//     tabIndex: disabled ? -1 : 0,
//     'data-disabled': disabled ? '' : undefined,
//     'aria-disabled': disabled,
//   };

//   if (asChild && React.isValidElement(children)) {
//     return React.cloneElement(children, {
//       ...itemProps,
//       className: `${children.props.className || ''} ${
//         itemProps.className
//       }`.trim(),
//     });
//   }

//   return <div {...itemProps}>{children}</div>;
// };

// // Separator component
// const DropdownMenuSeparator = ({ className = '' }) => (
//   <div
//     className={`dropdown-menu-separator ${className}`}
//     role="separator"
//     aria-orientation="horizontal"
//   />
// );

// // Label component
// const DropdownMenuLabel = ({ children, className = '', ...props }) => (
//   <div className={`dropdown-menu-label ${className}`} {...props}>
//     {children}
//   </div>
// );

// // Group component
// const DropdownMenuGroup = ({ children, className = '' }) => (
//   <div className={`dropdown-menu-group ${className}`} role="group">
//     {children}
//   </div>
// );

// // Checkbox Item component
// const DropdownMenuCheckboxItem = ({
//   children,
//   checked = false,
//   onCheckedChange,
//   disabled = false,
//   className = '',
//   ...props
// }) => {
//   const handleSelect = (e) => {
//     e.preventDefault();
//     if (!disabled) {
//       onCheckedChange?.(!checked);
//     }
//   };

//   return (
//     <DropdownMenuItem
//       {...props}
//       className={`dropdown-menu-checkbox-item ${className}`}
//       onSelect={handleSelect}
//       disabled={disabled}
//       role="menuitemcheckbox"
//       aria-checked={checked}
//     >
//       <span className="dropdown-menu-item-indicator">
//         {checked && (
//           <svg
//             width="15"
//             height="15"
//             viewBox="0 0 15 15"
//             fill="none"
//             xmlns="http://www.w3.org/2000/svg"
//           >
//             <path
//               d="m11.4669 3.72684c.2662.14348.3666.45126.2231.68771l-4.85711 8.15545c-.13999.2353-.42532.3492-.68999.2732s-.44079-.3107-.4519-.6004l-.66851-17.13303c-.00632-.16165.06301-.31516.17542-.38891.11241-.0737.25338-.08188.36943-.02082z"
//               fill="currentColor"
//               fillRule="evenodd"
//               clipRule="evenodd"
//             />
//           </svg>
//         )}
//       </span>
//       {children}
//     </DropdownMenuItem>
//   );
// };

// PropTypes definitions
DropdownMenu.propTypes = {
  children: PropTypes.node.isRequired,
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
  defaultOpen: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
};

DropdownMenuTrigger.propTypes = {
  children: PropTypes.node.isRequired,
  asChild: PropTypes.bool,
  isOpen: PropTypes.bool,
  onOpenChange: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

DropdownMenuContent.propTypes = {
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool,
  onOpenChange: PropTypes.func,
  align: PropTypes.oneOf(['start', 'center', 'end']),
  side: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
  sideOffset: PropTypes.number,
  alignOffset: PropTypes.number,
  className: PropTypes.string,
  avoidCollisions: PropTypes.bool,
  forceMount: PropTypes.bool,
};

// Named exports for individual components
export { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent };

// Compound component exports
DropdownMenu.Trigger = DropdownMenuTrigger;
DropdownMenu.Content = DropdownMenuContent;

// Default export
export default DropdownMenu;
